import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { camelizeKeys } from 'humps';
import { Logger } from 'pino';

export interface RequestParams<TData = unknown, TArgs = unknown> {
    args?: TArgs;
    headers?: { [key: string]: string };
    axiosConfig?: AxiosRequestConfig<TData>;
    logger?: Logger;
    options?: {
        skipCamelCase?: boolean;
    };
}

const clientMap = new Map<string, AxiosInstance>();

/**
 * Creates a base request function that can be used to make API requests
 *
 * @param baseUrl Base URL for requests
 * @param defaultHeaders Default headers to include in all requests
 */
export const baseRequest = <TData = unknown, TArgs = unknown>(
    baseUrl: string,
    defaultHeaders?: { [key: string]: string }
) => {
    return (
        method: 'get' | 'post' | 'put' | 'patch' | 'delete',
        path: string,
        params: RequestParams<TData, TArgs>
    ) =>
        request(method, baseUrl, path, {
            ...params,
            headers: {
                ...defaultHeaders,
                ...params.headers,
            },
        });
};

/**
 * Used to make an API request
 *
 * @param method HTTP method
 * @param baseUrl Base URL for the request
 * @param path Path to the endpoint
 * @param params Request parameters
 */
export const request = async <TData = unknown, TArgs = unknown>(
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    baseUrl: string,
    path: string,
    params: RequestParams<TData, TArgs>
) => {
    const apiClient: AxiosInstance = (clientMap.get(baseUrl) ??
        clientMap.set(baseUrl, axios.create({ baseURL: baseUrl })).get(baseUrl)) as AxiosInstance;

    const { args, headers, axiosConfig, logger } = params;
    path = path.startsWith('/') ? path : `/${path}`;

    try {
        let data;
        if (['get', 'delete'].includes(method)) {
            const response = await apiClient[method]<TData>(path, {
                ...axiosConfig,
                headers,
                params: args,
            });
            data = response.data;
        } else {
            const response = await apiClient[method]<TData>(path, args, {
                ...axiosConfig,
                headers,
            });
            data = response.data;
        }

        if (params.options?.skipCamelCase) {
            return data;
        } else {
            return camelizeKeys(data) as TData;
        }
    } catch (e: any) {
        logAndRethrowError(e, logger);
    }
};

/** *****************************************************
 ******************* OLD API CLIENT *********************
 ********************************************************/

/**
 * @deprecated Use {@link request} or {@link baseRequest} instead
 */
export const apiClient = axios.create({
    baseURL: '/',
    headers: {},
});

export const logAndRethrowError = (e: Error | AxiosError, log?: Logger): void => {
    const logger = log ?? console;

    if (axios.isAxiosError(e)) {
        if (e.response) {
            logger.error(
                `Got a ${e.response?.status} from ${e.config?.method} ${e.config?.url}: ${e.message}`
            );
            if (e.response.data) {
                const stringifiedResp = JSON.stringify(e.response.data, null, 2);
                // Only print the first 1,000 characters of the data to keep from overwhelming
                logger.error(stringifiedResp.substring(0, 1000));
                if (stringifiedResp.length > 1000) {
                    logger.error('...');
                }
            }
        } else if (e.request) {
            logger.error(`Making request ${e.config?.method} ${e.config?.url} failed`, e.message);
        }

        const err = e as Partial<AxiosError>;
        const errResponse = e.response as Partial<AxiosResponse>;

        delete err.request;
        delete err.config;
        if (errResponse) {
            delete errResponse.statusText;
            delete errResponse.headers;
            delete errResponse.config;
            delete errResponse.request;
        }
    } else {
        logger.error({
            type: 'unexpected',
            error: e,
        });
    }
    throw e;
};

/**
 * @deprecated Use {@link request} or {@link baseRequest} instead
 */
export const getDataPdf = async <T = any>(url: string, config?: AxiosRequestConfig<T>) => {
    try {
        const { data } = await apiClient.get<T>(url, config);
        return data;
    } catch (e: any) {
        logAndRethrowError(e);
    }
};

/**
 * @deprecated Use {@link request} or {@link baseRequest} instead
 */
export const getData = async <T = any>(url: string, config?: AxiosRequestConfig<T>, log?: Logger) => {
    try {
        const { data } = await apiClient.get<T>(url, config);
        return camelizeKeys(data) as T;
    } catch (e: any) {
        logAndRethrowError(e, log);
    }
};

/**
 * @deprecated Use {@link request} or {@link baseRequest} instead
 */
export const getDataNoCamelize = async <T = any>(
    url: string,
    config?: AxiosRequestConfig<T>,
    log?: Logger
) => {
    try {
        const { data } = await apiClient.get<T>(url, config);
        return data;
    } catch (e: any) {
        logAndRethrowError(e, log);
    }
};

/**
 * @deprecated Use {@link request} or {@link baseRequest} instead
 */
export const getDataWithBody = async <TData = object, TArgs = object>(
    url: string,
    _args?: TArgs,
    config?: object,
    log?: Logger
) => {
    try {
        const { data } = await apiClient.get(url, config);
        return camelizeKeys(data) as TData;
    } catch (e: any) {
        logAndRethrowError(e, log);
    }
};

/**
 * @deprecated Use {@link request} or {@link baseRequest} instead
 */
export const postData = async <TData = object, TArgs = object>(
    url: string,
    args?: TArgs,
    config?: AxiosRequestConfig<TArgs>,
    log?: Logger
) => {
    try {
        const { data } = await apiClient.post<TData>(url, args, config);
        return data;
    } catch (e: any) {
        logAndRethrowError(e, log);
    }
};

/**
 * @deprecated Use {@link request} or {@link baseRequest} instead
 */
export const putData = async <TData = object, TArgs = object>(
    url: string,
    args?: TArgs,
    config?: AxiosRequestConfig<TArgs>,
    log?: Logger
) => {
    try {
        const { data } = await apiClient.put<TData>(url, args, config);
        return data;
    } catch (e: any) {
        logAndRethrowError(e, log);
    }
};

/**
 * @deprecated Use {@link request} or {@link baseRequest} instead
 */
export const patchData = async <TData = object, TArgs = object>(
    url: string,
    args?: TArgs,
    config?: AxiosRequestConfig<TArgs>,
    log?: Logger
) => {
    try {
        const { data } = await apiClient.patch<TData>(url, args, config);
        return data;
    } catch (e: any) {
        logAndRethrowError(e, log);
    }
};

/**
 * @deprecated Use {@link request} or {@link baseRequest} instead
 */
export const deleteData = async <TData = object, TArgs = object>(
    url: string,
    config?: AxiosRequestConfig<TArgs>,
    log?: Logger
) => {
    try {
        const { data } = await apiClient.delete<TData>(url, config);
        return data;
    } catch (e: any) {
        logAndRethrowError(e, log);
    }
};

export const apiPath =
    typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}/api` : '/api';
